import sanitizeHtml from 'sanitize-html';

import { getItemByLetter, getItemDetail } from 'lib/api/glossary';
import { glossaryMetaTags } from 'global/SEO/constants';
import {
  IGlossaryData,
  IParamsContext,
  IParamsResult,
  GlossaryServerSideResult,
} from 'features/glosarry/types';

import { DEFAULT_LETTER, MAX_CHARS } from 'features/glosarry/constants';

const isValidTerms = (terms: IGlossaryData[]): boolean =>
  Array.isArray(terms) && terms.length > 0;

const isValidGlossary = (glossary: IGlossaryData): boolean =>
  glossary?.name !== undefined && glossary?.definition !== undefined;

const getLetterParameters = (params: IParamsContext): IParamsResult => {
  const letter: string =
    params?.slug?.[0] || params?.letter?.[0] || DEFAULT_LETTER;
  const slug: string = params?.slug;

  return {
    letter: letter.toLowerCase(),
    slug,
  };
};

const generateAlphabet = (): string[] => {
  const firstAlphabet = 'a'.charCodeAt(0);
  const alphabetArr: string[] = Array.from({ length: 26 }, (_, index) =>
    String.fromCharCode(firstAlphabet + index),
  );
  alphabetArr.unshift('#');

  return alphabetArr;
};

const getGlossariesByLetter = async (
  letter: string,
): Promise<IGlossaryData[] | null> => {
  const res = await getItemByLetter(letter);
  const { data } = res?.data || {};
  return data || null;
};

const getGlossaryBySlug = async (
  slug: string,
): Promise<IGlossaryData | null> => {
  const res = await getItemDetail(slug);
  const { data } = res?.data || {};
  return data || null;
};

const getDefaultGlossary = (slug: string | null): IGlossaryData => ({
  name: '',
  definition: '',
  explanation: '',
  formula: '',
  alias: slug || '',
  meta: {
    title: glossaryMetaTags.title,
    description: glossaryMetaTags.description,
    keyword: glossaryMetaTags.keywords,
  },
});

const getGlossaryServerSide = async (
  params: IParamsContext,
  passDetail = false,
): Promise<GlossaryServerSideResult> => {
  const { letter, slug } = getLetterParameters(params);
  let glossaries: IGlossaryData[] | null = null;
  let glossary: IGlossaryData | null = getDefaultGlossary(slug);

  try {
    if (letter && !passDetail) {
      glossaries = await getGlossariesByLetter(letter);
    }

    if (isValidTerms(glossaries)) {
      const selectedGlossary = slug
        ? glossaries.find(({ alias }) => alias === slug)
        : glossaries[0];

      if (selectedGlossary) {
        glossary = selectedGlossary;
      }
    } else if (slug) {
      glossary = await getGlossaryBySlug(slug);
    }

    if (glossary) {
      const { definition, alias, meta } = glossary;

      let description = sanitizeHtml(meta.description || definition, {
        allowedTags: [],
      });

      if (description?.length > MAX_CHARS) {
        let trimmedString = description.substring(0, MAX_CHARS - 1);

        const lasStringIndex = Math.min(
          trimmedString.length,
          trimmedString.lastIndexOf(' '),
        );
        trimmedString = `${trimmedString.substring(0, lasStringIndex)}...`;

        description = trimmedString.substring(0, MAX_CHARS - 1);
      }

      glossary.alias = alias || slug;
      glossary.meta = {
        title: meta.title || glossaryMetaTags.title,
        description: description || glossaryMetaTags.description,
        keyword: meta.keyword || glossaryMetaTags.keywords,
      };
    }
  } catch (error) {
    console.error(error);
  }

  return { glossaries, glossary };
};

const generateMetaKeywords = (keyword: string): string => {
  const keywords = [
    keyword,
    `pengertian ${keyword}`,
    `apa itu ${keyword}`,
    `apa yang dimaksud dengan ${keyword}`,
    `${keyword} adalah`,
    `${keyword} artinya`,
    `definisi ${keyword}`,
    `${keyword} saham`,
    `${keyword} market`,
    `${keyword} definition`,
    `${keyword} definisi`,
    `${keyword} meaning`,
    `${keyword} formula`,
    `${keyword} contoh`,
    `${keyword} example`,
  ];

  const metaKeywords = keywords.join(', ').toLowerCase();

  return metaKeywords;
};

export {
  isValidTerms,
  isValidGlossary,
  getLetterParameters,
  generateAlphabet,
  getGlossaryServerSide,
  generateMetaKeywords,
};
